import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Maximize2 } from "lucide-react";
import { KABAS, FIT, PRICE } from "@/data/collection";
import { Reveal, Eyebrow, EASE } from "./Reveal";
import { Lightbox } from "./Lightbox";

const photosOf = (k) => [{ src: k.image, alt: k.alt, label: `${k.name} · worn` }, ...k.gallery.map((g) => ({ ...g, label: `${k.name} · ${g.label}` }))];
const CLOSET = [{ src: "/images/real/closet_three.jpg", alt: "Crimson Cape, Midnight Majesty and Black Mamba hanging in a wooden closet", label: "The closet" }];

const Frame = ({ src, alt, label, className = "", testId, onOpen }) => (
  <figure className={`group relative cursor-zoom-in overflow-hidden border border-gold/20 bg-ink2 ${className}`} data-testid={testId} onClick={onOpen}>
    <img src={src} alt={alt} loading="lazy"
      className="h-full w-full object-cover object-top transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]" />
    <div className="spotlight absolute inset-0 opacity-50 transition-opacity duration-700 group-hover:opacity-20" />
    <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 bg-ink/60 text-gold opacity-90 backdrop-blur-md transition-transform duration-300 group-hover:scale-110 lg:opacity-0 lg:group-hover:opacity-100" aria-hidden="true">
      <Maximize2 className="h-4 w-4" />
    </span>
    {label && (
      <figcaption className="absolute bottom-3 left-3 rounded-full border border-gold/30 bg-ink/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cream backdrop-blur-md">
        {label}
      </figcaption>
    )}
  </figure>
);

const spanFor = (n, i) => (n % 3 === 0 ? "sm:col-span-2" : n % 3 === 1 ? (i < n - 4 ? "sm:col-span-2" : "sm:col-span-3") : i < 2 ? "sm:col-span-3" : "sm:col-span-2");

const Piece = ({ kaba, flip, onOrder, onOpen }) => (
  <motion.article initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 1.1, ease: EASE }} data-testid={`kaba-card-${kaba.id}`}
    className={`flex flex-col gap-4 lg:min-h-[760px] ${flip ? "lg:flex-row-reverse" : "lg:flex-row"}`}>
    <div className="relative lg:w-1/2">
      <Frame src={kaba.image} alt={kaba.alt} className="aspect-[3/4] lg:absolute lg:inset-0 lg:aspect-auto" testId={`kaba-main-${kaba.id}`} onOpen={() => onOpen(photosOf(kaba), 0)} />
      <span className="pointer-events-none absolute left-5 top-4 font-display text-6xl italic text-white/90">{kaba.no}</span>
    </div>
    <div className="flex flex-col gap-4 lg:w-1/2">
      <div className="border border-gold/20 bg-ink2 p-6 sm:p-8">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h3 className="font-display text-3xl leading-none text-cream sm:text-4xl">{kaba.name}</h3>
          <p className="font-display text-3xl text-gold" data-testid={`kaba-price-${kaba.id}`}>{PRICE}</p>
        </div>
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{kaba.line}</p>
        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-cream" data-testid={`kaba-fit-${kaba.id}`}>{FIT}</p>
        <p className="mt-5 text-sm font-light leading-relaxed text-stone2 sm:text-base">{kaba.text}</p>
        <button onClick={() => onOrder(kaba.id)} data-testid={`order-kaba-${kaba.id}`}
          className="group mt-6 inline-flex items-center gap-3 rounded-full border border-gold/60 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-gold transition-[background-color,color] duration-300 hover:bg-gold hover:text-ink">
          Order this kaba <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
        </button>
      </div>
      <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-6">
        {kaba.gallery.map((g, i) => (
          <Frame key={g.src} {...g} className={`aspect-[3/4] sm:aspect-auto sm:min-h-[320px] ${spanFor(kaba.gallery.length, i)}`} testId={`kaba-gallery-${kaba.id}-${i}`} onOpen={() => onOpen(photosOf(kaba), i + 1)} />
        ))}
      </div>
    </div>
  </motion.article>
);

const Closet = ({ onOpen }) => (
  <Reveal className="relative overflow-hidden border border-gold/25" >
    <div data-testid="closet-feature" className="relative aspect-[4/5] cursor-zoom-in sm:aspect-[16/9]" onClick={() => onOpen(CLOSET, 0)}>
      <img src="/images/real/closet_three.jpg" alt="Three kabas hanging side by side in a wooden closet"
        className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-12">
        <Eyebrow>The closet</Eyebrow>
        <p className="mt-4 max-w-xl font-display text-3xl leading-tight text-cream sm:text-5xl">
          Three kabas, hung like <span className="italic text-white">heirlooms</span>.
        </p>
      </div>
    </div>
  </Reveal>
);

export const Lookbook = ({ onOrder }) => {
  const [box, setBox] = useState({ images: [], index: null });
  const openBox = (images, index) => setBox({ images, index });
  return (
  <section id="collection" data-testid="collection-section" className="mx-auto max-w-[1400px] px-5 py-28 sm:px-10 lg:py-36">
    <div className="mb-14 flex flex-col justify-between gap-6 border-b border-gold/15 pb-8 md:flex-row md:items-end">
      <Reveal>
        <Eyebrow>New · Hilmay’s Kabas</Eyebrow>
        <h2 className="mt-5 font-display text-4xl font-light tracking-tight text-cream sm:text-5xl lg:text-6xl">
          The new <span className="italic text-white">modern</span> kabas
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="max-w-sm text-sm font-light leading-relaxed text-stone2 sm:text-base">
          Three capes for the woman who knows her worth — one size, one price: {PRICE}. Tap any photo to zoom in.
        </p>
      </Reveal>
    </div>
    <div className="space-y-16 lg:space-y-24">
      {KABAS.map((k, i) => <Piece key={k.id} kaba={k} flip={i % 2 === 1} onOrder={onOrder} onOpen={openBox} />)}
      <Closet onOpen={openBox} />
    </div>
    <Lightbox images={box.images} index={box.index}
      setIndex={(index) => setBox((b) => ({ ...b, index }))} onClose={() => setBox((b) => ({ ...b, index: null }))} />
  </section>
  );
};
